<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CloseIcon } from '@solar-icons/vue/linear'
import AppParticleRing from '../../primitives/AppParticleRing/AppParticleRing.vue'
import AppDrawnCheck from '../../primitives/AppDrawnCheck/AppDrawnCheck.vue'
import ActivitySearchGroup from './ActivitySearchGroup.vue'
import ActivitySourceRows from './ActivitySourceRows.vue'
import {
  ACTIVITY_KIND_META,
  isFinished,
  type ActivityItem as ActivityItemType,
  type ActivityKind,
  type ActivityStatus,
} from './activity-thread-types'
import { endsNow } from './activity-summary'
import type { AgentCopyParams, AgentLabelKey, AgentSource } from '@/types/chat'
import { agentLabelParams } from '@/lib/chat/agent-label'

const props = defineProps<{
  /** Which activity kind this step represents; drives the default icon + label. */
  kind: ActivityKind
  labelKey?: AgentLabelKey
  labelParams?: AgentCopyParams
  description?: string
  status: ActivityStatus
  /** What the step read, shown as rows once its result arrives. */
  sources?: readonly AgentSource[]
  /** Consecutive web searches this one step stands for; each shows its query and results in a well. */
  searches?: readonly ActivityItemType[]
  /** True for the final item in the thread - suppresses the connector line and bottom padding. */
  isLast?: boolean
}>()

const { t } = useI18n()

const glyph = computed(() => ACTIVITY_KIND_META[props.kind].icon)
const resolvedLabel = computed(() => {
  if (props.searches) return t('chat.activity.searchGroup', { count: props.searches.length })
  return props.labelKey
    ? t(
        `chat.activity.label.${props.labelKey}`,
        agentLabelParams(props.labelKey, props.labelParams),
      )
    : t(ACTIVITY_KIND_META[props.kind].labelKey)
})
const statusSrText = computed(() => {
  switch (props.status) {
    case 'active':
      return t('chat.activity.inProgress')
    case 'complete':
      return t('chat.activity.done')
    case 'failed':
      return t('chat.activity.failed')
    case 'pending':
      return t('chat.activity.pending')
  }
  return t('chat.activity.pending')
})

const finished = computed(() => isFinished(props.status))
const failed = computed(() => props.status === 'failed')

const landed = ref(finished.value)
const finishedLive = ref(false)
let landFrame = 0

watch(
  () => props.status,
  (next, previous) => {
    if (!endsNow(previous, next)) {
      landed.value = isFinished(next)
      return
    }
    finishedLive.value = true
    landed.value = false
    cancelAnimationFrame(landFrame)
    landFrame = requestAnimationFrame(() => {
      landed.value = true
    })
  },
)

onBeforeUnmount(() => cancelAnimationFrame(landFrame))

const labelClass = computed(() => {
  switch (props.status) {
    case 'active':
      return 'text-text-primary'
    case 'complete':
      return 'text-text-secondary'
    case 'failed':
      return 'text-accent-red'
    case 'pending':
      return 'text-text-tertiary'
  }
  return 'text-text-tertiary'
})
</script>

<template>
  <div
    role="listitem"
    class="activity-step grid grid-rows-[1fr]"
    :data-status="status"
    :aria-label="`${resolvedLabel}, ${statusSrText}`"
  >
    <div class="flex min-h-0 min-w-0 gap-4">
      <div class="flex w-12 shrink-0 flex-col items-center gap-1">
        <span class="icon-sm relative grid size-12 shrink-0 place-items-center">
          <Transition
            leave-active-class="transition-[opacity,scale] duration-slow ease-soft-exit"
            leave-to-class="opacity-0 scale-30"
          >
            <AppParticleRing
              v-if="status === 'active'"
              variant="mark"
              class="col-start-1 row-start-1"
            />
          </Transition>
          <component
            :is="glyph"
            v-if="status === 'pending'"
            aria-hidden="true"
            class="col-start-1 row-start-1 text-icon-tertiary"
          />
          <span
            v-if="finished"
            data-part="done"
            class="relative col-start-1 row-start-1 grid size-12 place-items-center rounded-full transition-[opacity,scale] duration-slower ease-spring-bouncy"
            :class="[
              landed ? 'scale-100 opacity-100' : 'scale-40 opacity-0',
              failed ? 'bg-accent-red-soft text-accent-red' : 'bg-bg-secondary text-text-secondary',
            ]"
          >
            <CloseIcon v-if="failed" aria-hidden="true" />
            <AppDrawnCheck v-else :drawn="landed" :animated="finishedLive" />
            <span
              v-if="finishedLive && landed"
              aria-hidden="true"
              class="activity-ripple absolute inset-0 rounded-full"
            />
          </span>
        </span>
        <span
          aria-hidden="true"
          class="w-0 flex-1 origin-top border-l transition-[scale,border-color] duration-slower ease-spring"
          :class="[
            isLast ? 'scale-y-0' : 'scale-y-100',
            finished ? 'border-border-strong' : 'border-border',
          ]"
        />
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-1" :class="{ 'pb-8': !isLast }">
        <p class="flex min-h-12 min-w-0 items-center text-body-sm" :class="labelClass">
          <span class="truncate" :title="resolvedLabel">{{ resolvedLabel }}</span>
        </p>
        <p v-if="description" class="truncate text-body-sm text-text-tertiary" :title="description">
          {{ description }}
        </p>
        <ActivitySearchGroup v-if="searches" :searches="searches" />
        <ActivitySourceRows v-else-if="sources?.length" :sources="sources" />
      </div>
    </div>
  </div>
</template>
